import { CardContent } from '@mui/material';

import React, { useContext, useMemo } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { Contact, Location, Note, TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';

import { Card } from '_lib/components/Card/Card';
import { ErrorIconBadge } from '_lib/components/ErrorIconBadge/ErrorIconBadge';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { CustomerContext } from '_lib/types/PhoneCall.types';
import { formatAddressTwoLine, customerDisplayName } from '_lib/utils/helpers';

import { Contacts } from '../Contacts/Contacts';
import { LocationsCard } from '../LocationsCard/LocationsCard';
import { NotesCard } from '../NotesCard/NotesCard';

export interface CustomerDetailDisplayProps {
  companyId: string;
  customerContext: CustomerContext;
  locationContactsMap: {
    [locationProviderId: string]: Contact[];
  };
  timezone: TimezoneAbbreviationEnum;
  onContactsUpdated: (contacts: Contact[]) => void;
  onLocationsUpdated: (locations: Location[]) => void;
  onLocationContactsUpdated: (locationProviderId: string, contacts: Contact[]) => void;
  onNotesUpdated: (notes: Note[]) => void;
}

export const CustomerDetailDisplay = (props: CustomerDetailDisplayProps): React.ReactElement => {
  const {
    companyId,
    customerContext,
    locationContactsMap,
    timezone,
    onContactsUpdated,
    onLocationsUpdated,
    onLocationContactsUpdated,
    onNotesUpdated
  } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);
  const serviceTitanCustomerResources = useMemo(
    () => ({
      contacts: serviceTitanApi.customers.contacts(customerContext.customer.providerId),
      notes: serviceTitanApi.customers.notes(customerContext.customer.providerId)
    }),
    [customerContext.customer.providerId, serviceTitanApi.customers]
  );

  return (
    <FlexBox sx={{ px: 3, py: 2 }}>
      <Stack useFlexGap sx={{ flexGrow: 1 }}>
        <Card variant='outlined'>
          <CardContent>
            {customerContext.customer.isDNS && <ErrorIconBadge text='DNS' sx={{ marginBottom: 1 }} />}
            <Typography variant='h3' text={customerDisplayName(customerContext.customer)} />
            <Typography
              text={formatAddressTwoLine(customerContext.customer, { includeCountry: true })}
              sx={{ whiteSpace: 'pre-line' }}
            />
            <Contacts
              contacts={customerContext.customer.contacts}
              contactsResource={serviceTitanCustomerResources.contacts}
              onContactsUpdated={onContactsUpdated}
            />
          </CardContent>
        </Card>
        <LocationsCard
          companyId={companyId}
          providerCustomerId={customerContext.customer.providerId}
          locations={customerContext.locations}
          locationContactsMap={locationContactsMap}
          onLocationsUpdated={onLocationsUpdated}
          onLocationContactsUpdated={onLocationContactsUpdated}
        />
        <NotesCard
          notes={customerContext.customer.notes}
          notesResource={serviceTitanCustomerResources.notes}
          timezone={timezone}
          onNotesUpdated={onNotesUpdated}
        />
      </Stack>
    </FlexBox>
  );
};
